<template>
  <WdsStack
    direction-row
    gap-sm
    align-center
    class="tw-px-2 tw-flex-wrap lg:tw-flex-nowrap tw-justify-end"
  >
    <slot name="action">
      <WdsAlert info>
        <WdsStack direction-row gap-sm justify-space-between align-center>
          <div>
            <WdsText>
              <span class="tw-text-indigo-500">
                {{ offerStatusTitle }}
              </span>
            </WdsText>
            <div class="tw-text-gray-800">
              {{
                completed
                  ? $t(
                      hasRecruiterManagingWorkers
                        ? 'Some actions may still need to be completed before the staffing agency can sign the contract.'
                        : 'Some actions may still need to be completed before the worker can sign the contract.',
                    )
                  : $t('The hire will be ready to offer once the outstanding actions below are complete.')
              }}
            </div>
          </div>
          <WdsStack class="tw-whitespace-nowrap" align-start>
            <WdsButton
              primary
              :disabled="!canShare"
              class="tw-justify-center tw-items-center"
              @click="showShareHireModal = true"
            >
              {{ $t('Finalise offer') }}
              <WdsIcon name="chevron-right" :size="16" class="tw-ml-1" />
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </WdsAlert>
    </slot>
  </WdsStack>

  <HireComplianceGateStatus :compliances="active" :loading="loading" :hire-id="hireId" />

  <ShareHireModal
    :open="showShareHireModal"
    :hire-id="hireId"
    @close="showShareHireModal = false"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { WdsAlert, WdsButton, WdsIcon, WdsStack, WdsText } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { useHireRecruiter } from '../composables/useHireRecruiter'
import { graphql } from '@/gql'
import { ComplianceName } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import HireComplianceGateStatus from '@/components/Hire/HirePageCompliance/HireComplianceGateStatus.vue'
import { useComplianceGate } from '@/components/Hire/HirePageCompliance/useComplianceGates'
import ShareHireModal from '@/components/Hire/ShareHireModal.vue'

const props = defineProps<{
  hireId: string
}>()

const showShareHireModal = ref<boolean>(false)

const { hasRecruiterManagingWorkers } = useHireRecruiter(props.hireId)

// The offer gate now handles the "waiting on staffing agency" case
// itself (SupplierHirePreparedCompliance), so `completed` alone is the
// right signal for the button.
const { active, completed, loading } = useComplianceGate(
  props.hireId,
  ComplianceName.GateHireOffer,
)

const canShare = completed

const { result: invitedResult } = useQuery(
  graphql(`
    query HireDraftStatusWorkerInvited($id: ID!) {
      hire(id: $id) {
        id
        workerInvited
      }
    }
  `),
  { id: props.hireId },
  { clientId: 'publicApi' },
)

const offerStatusTitle = computed(() => {
  if (invitedResult.value?.hire?.workerInvited) {
    return hasRecruiterManagingWorkers.value
      ? $t('The staffing agency can see this draft to answer your questions')
      : $t('The worker can see this draft to answer your questions')
  }

  return hasRecruiterManagingWorkers.value
    ? $t('Offer is not shared with the staffing agency yet')
    : $t('Offer is not shared with the worker yet')
})
</script>
